import {
    Box,
    Button,
    Divider,
    Grid,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { Tiny } from 'components/Typography';
import CartContext from 'contexts/CartContext';
import useAuth from 'hooks/useAuth';
import Layers from 'icons/Layers';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import formatCurrency from 'utils/formatCurrency';

const ChoosePackage = () => {
    const [data, setData] = useState([]);
    const navigate = useNavigate();
    const cart = useContext(CartContext);
    const { user } = useAuth();

    const handleChoosePackage = (packageData) => {
        if (!packageData) return;

        cart.clearCart();
        cart.setPackage(packageData);
        cart.setTotal(parseInt(packageData.sympoPrice) || 0);
        cart.setSympoPrice(
            parseInt(
                data.find(
                    (item) =>
                        item.isBundle &&
                        0 == item.wsFullDayIncludes &&
                        0 == item.wsHalfDayIncludes,
                )?.price || 0,
            ),
        );

        if (
            packageData.wsFullDayIncludes ||
            packageData.wsHalfDayIncludes ||
            !packageData.isBundle
        ) {
            navigate('/dashboard/choose-workshop');
        } else {
            navigate('/dashboard/payment');
        }
    };

    useEffect(() => {
        const getData = async () => {
            let shouldStop = false;
            await axiosInstance
                .get('/api/prv/member-invoices', {
                    params: {
                        packageType: 'main',
                    },
                })
                .then((res) => {
                    const data = res?.data || [];
                    const pendingInvoice = data.find(
                        (item) => 'pending' === item.status.value,
                    );
                    const paidInvoice = data.find(
                        (item) => 'paid' === item.status.value,
                    );
                    const found = pendingInvoice || paidInvoice;
                    if (found) {
                        cart.setFromInvoice(found);
                        navigate('/dashboard/payment-complete');
                        shouldStop = true;
                    }
                })
                .catch((err) => console.log(err));

            if (shouldStop) return;

            await axiosInstance
                .get('/api/prv/packages', {
                    params: {
                        type: 'main'
                    }
                })
                .then((res) => {
                    setData(res.data);
                })
                .catch((err) => console.log(err));
        };

        getData();
    }, [navigate]);

    return (
        <DashboardLayout>
            <Box pt={10}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography
                            align="center"
                            variant="h5"
                            fontWeight={600}
                        >
                            Choose Package
                        </Typography>
                        {user?.hasDiscount && (
                            <Typography
                                align="center"
                                sx={{ mt: 1 }}
                                color="success.main"
                                fontWeight={600}
                            >
                                You are eligible for a symposium discount (30%)
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Grid
                            container
                            spacing={3}
                            justifyContent="center"
                            alignItems="stretch"
                        >
                            {[...data]
                                .sort((a, b) => a.sequence - b.sequence)
                                .map((item, key) => (
                                    <Grid item key={key} xs={12} sm={6} md={4}>
                                        <SingleCard
                                            data={item}
                                            choosePackage={handleChoosePackage}
                                            type="Month"
                                            Icon={Layers}
                                            iconColor="warning"
                                        />
                                    </Grid>
                                ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </DashboardLayout>
    );
};

export default ChoosePackage;

function SingleCard({ data, choosePackage }) {
    const theme = useTheme();
    const handleChoose = (data) => {
        choosePackage(data);
    };
    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 3,
                height: '100%',
                maxWidth: 360,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                border: `1px solid ${theme.palette.divider}`,
                transition:
                    'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                backgroundColor: theme.palette.background.paper,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                    borderColor: theme.palette.primary.main,
                },
            }}
        >
            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {data.name}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1.5} sx={{ mb: 2 }}>
                {data.benefits.map((benefit, key) => (
                    <Tiny key={key} color="text.secondary" fontSize={13}>
                        {`• ${benefit}`}
                    </Tiny>
                ))}
            </Stack>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 'auto',
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    {formatCurrency(parseInt(data.price))}
                    {!data.isBundle && <sup>+</sup>}
                </Typography>
                <Button
                    variant="contained"
                    color={'secondary'}
                    onClick={() => handleChoose(data)}
                    size="small"
                    sx={{
                        borderRadius: 2,
                        px: 2.5,
                        textTransform: 'uppercase',
                    }}
                    fullWidth={false}
                >
                    Choose Package
                </Button>
            </Box>
        </Box>
    );
}
